import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { UsersThree, Link as LinkIcon, Copy, CheckCircle } from "@phosphor-icons/react";

export default function TeamAccessManager() {
  const [team, setTeam] = useState([]);
  const [busy, setBusy] = useState("");
  const [links, setLinks] = useState({});
  const [newMember, setNewMember] = useState({ email: "", full_name: "", role: "comptable" });
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    api.get("/auth/team").then((r) => setTeam(r.data.team || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const addMember = async () => {
    if (!newMember.email.trim() || !newMember.email.includes("@")) { toast.error("Courriel invalide."); return; }
    setAdding(true);
    try {
      const r = await api.post("/auth/team", newMember);
      toast.success(`Compte ${r.data.role_label} créé pour ${r.data.email}. Génère maintenant son lien d'invitation.`);
      setNewMember({ email: "", full_name: "", role: "comptable" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de créer le compte.");
    } finally { setAdding(false); }
  };

  const generate = async (email) => {
    setBusy(email);
    try {
      const r = await api.post("/auth/invitations", { email, base_url: window.location.origin });
      setLinks((p) => ({ ...p, [email]: r.data }));
      toast.success(r.data.emailed ? "Lien généré et envoyé par courriel." : "Lien généré (copie-le pour le partager).");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la génération du lien.");
    } finally { setBusy(""); }
  };

  const copy = (email) => {
    const link = links[email]?.link;
    if (!link) return;
    navigator.clipboard.writeText(link);
    toast.success("Lien copié !");
  };

  return (
    <div className="hard-border" data-testid="team-access-section">
      <div className="px-5 py-4 bg-foreground text-white flex items-center gap-2">
        <UsersThree size={18} weight="bold" />
        <div className="ticker">Équipe & accès (RBAC)</div>
      </div>
      <div className="p-5 space-y-3">
        <p className="text-sm text-muted-foreground">
          Génère un lien d'invitation sécurisé (valide 72 h) pour que chaque membre d'équipe définisse <strong>son propre mot de passe</strong>. Plus besoin de partager un mot de passe par défaut.
        </p>

        {/* Ajouter un membre d'équipe (ex. comptable externe avec son propre courriel) */}
        <div className="rounded-lg border border-border p-4 space-y-3 bg-secondary/30" data-testid="add-team-member">
          <div className="text-sm font-semibold">Ajouter un membre d'équipe</div>
          <div className="grid sm:grid-cols-3 gap-2">
            <input value={newMember.email} onChange={(e) => setNewMember((s) => ({ ...s, email: e.target.value }))} placeholder="Courriel (ex. David.Dumais@mnp.ca)" data-testid="new-member-email" className="bg-background border border-border rounded px-3 py-2 text-sm" />
            <input value={newMember.full_name} onChange={(e) => setNewMember((s) => ({ ...s, full_name: e.target.value }))} placeholder="Nom complet" data-testid="new-member-name" className="bg-background border border-border rounded px-3 py-2 text-sm" />
            <select value={newMember.role} onChange={(e) => setNewMember((s) => ({ ...s, role: e.target.value }))} data-testid="new-member-role" className="bg-background border border-border rounded px-3 py-2 text-sm">
              <option value="comptable">Comptable</option>
              <option value="coach">Coach</option>
              <option value="reception">Réception</option>
              <option value="reception_prospects">Réception — Prospects et cours d'essai (pilote 30 j)</option>
              <option value="marketing">Marketing</option>
            </select>
          </div>
          <button onClick={addMember} disabled={adding} data-testid="add-team-member-btn" className="text-sm px-4 py-2 rounded-lg bg-foreground text-white disabled:opacity-60">{adding ? "Création…" : "Créer le compte"}</button>
        </div>

        {team.length === 0 && <p className="text-sm text-muted-foreground italic">Aucun compte d'équipe. Ajoute-en un ci-dessus.</p>}
        {team.map((m) => (
          <div key={m.email} data-testid={`team-row-${m.role}`} className="border border-border rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-semibold flex items-center gap-2 flex-wrap">
                <span className="truncate max-w-[45%]">{m.full_name}</span>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 border border-amber-500/30">{m.role_label}</span>
                {m.password_customized
                  ? <span className="text-[10px] text-green-600 flex items-center gap-0.5"><CheckCircle size={12} weight="fill" /> mot de passe défini</span>
                  : <span className="text-[10px] text-orange-500">mot de passe par défaut</span>}
              </div>
              <div className="text-xs text-muted-foreground truncate">{m.email}{m.expires_at ? ` · expire ${m.expires_at.slice(0, 10)}` : ""}{m.revoked_at ? " · 🔴 RÉVOQUÉ" : ""}{m.role === "reception_prospects" && !m.confidentiality_accepted_at && !m.revoked_at ? " · confidentialité non acceptée" : ""}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {m.role === "reception_prospects" && (
                <button onClick={async () => { if (!window.confirm(m.revoked_at ? `Réactiver l'accès de ${m.full_name} ?` : `RÉVOQUER immédiatement l'accès de ${m.full_name} ?`)) return; await api.post(`/auth/team/${m.id}/access`, { revoke: !m.revoked_at }); load(); }}
                  className={`text-xs px-2 py-1 rounded border ${m.revoked_at ? "border-green-500/40 text-green-600" : "border-red-500/40 text-red-500"}`} data-testid={`team-revoke-${m.id}`}>{m.revoked_at ? "Réactiver" : "Révoquer"}</button>
              )}
              {links[m.email] && (
                <button onClick={() => copy(m.email)} data-testid={`team-copy-${m.role}`}
                  className="px-3 py-2 rounded-lg border border-border text-xs font-semibold flex items-center gap-1.5 hover:bg-secondary">
                  <Copy size={13} /> Copier le lien
                </button>
              )}
              <button onClick={() => generate(m.email)} disabled={busy === m.email} data-testid={`team-invite-${m.role}`}
                className="px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 text-xs font-bold flex items-center gap-1.5 disabled:opacity-50">
                <LinkIcon size={13} weight="bold" /> {busy === m.email ? "…" : "Générer un lien"}
              </button>
            </div>
            {links[m.email]?.link && (
              <div className="w-full text-[11px] text-muted-foreground break-all bg-secondary/40 rounded p-2 font-mono">{links[m.email].link}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
